import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

export default function ARNecklace() {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const [showMessage, setShowMessage] = useState(true);
    const lastFaceTimeRef = useRef(Date.now());

    let scene, camera, renderer;
    let necklace = null;
    let animationId = null;

    // 🔴 STABLE ANCHOR REFS
    const neckAnchorRef = useRef(null);
    const smoothPosRef = useRef(new THREE.Vector3());

    useEffect(() => {
        initThree();
        loadNecklace();
        initFaceTracking();
        animate();

        window.addEventListener("resize", resizeRenderer);
        window.addEventListener("orientationchange", resizeRenderer);

        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener("resize", resizeRenderer);
            window.removeEventListener("orientationchange", resizeRenderer);
        };
    }, []);

    // =========================
    // THREE SETUP
    // =========================
    function initThree() {
        scene = new THREE.Scene();

        camera = new THREE.PerspectiveCamera(45, 1, 0.01, 50);
        camera.position.z = 1;

        renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            alpha: true,
            antialias: true,
            powerPreference: "high-performance"
        });

        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.1;
        renderer.physicallyCorrectLights = true;

        scene.add(new THREE.AmbientLight(0xffffff, 1));

        const light = new THREE.DirectionalLight(0xffffff, 1.5);
        light.position.set(0, 1, 2);
        scene.add(light);
    }

    // =========================
    // LOAD NECKLACE (AUTO SIZE)
    // =========================
    function loadNecklace() {
        new GLTFLoader().load("/necklace.glb", (gltf) => {
            necklace = gltf.scene;
            necklace.visible = false;

            const box = new THREE.Box3().setFromObject(necklace);
            const size = box.getSize(new THREE.Vector3());
            const scale = 1 / Math.max(size.x, size.y, size.z);

            necklace.scale.setScalar(scale);
            necklace.position.sub(box.getCenter(new THREE.Vector3()).multiplyScalar(scale));

            necklace.traverse((o) => {
                if (o.isMesh) {
                    if (o.material.map) {
                        o.material.map.colorSpace = THREE.SRGBColorSpace;
                    }
                    o.material.metalness = 0.85;
                    o.material.roughness = 0.25;
                    o.material.side = THREE.DoubleSide;
                }
            });

            scene.add(necklace);
        });
    }

    // =========================
    // FACE TRACKING
    // =========================
    async function initFaceTracking() {
        const faceMesh = new window.FaceMesh({
            locateFile: (file) =>
                `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
        });

        faceMesh.setOptions({
            maxNumFaces: 1,
            refineLandmarks: true,
            minDetectionConfidence: 0.7,
            minTrackingConfidence: 0.7
        });

        faceMesh.onResults(onResults);

        const stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: "user",
                width: { ideal: 720 },
                height: { ideal: 1280 }
            }
        });

        videoRef.current.srcObject = stream;

        videoRef.current.onloadedmetadata = () => {
            resizeRenderer();
            setTimeout(resizeRenderer, 300);
        };

        const cam = new window.Camera(videoRef.current, {
            onFrame: async () => {
                await faceMesh.send({ image: videoRef.current });
            }
        });

        cam.start();
    }

    // =========================
    // LANDMARK → WORLD
    // =========================
    function landmarkToWorld(x, y, z = -0.6) {
        return new THREE.Vector3(
            x * 2 - 1,
            -(y * 2 - 1),
            z
        ).unproject(camera);
    }

    // =========================
    // FACE RESULTS (STABLE)
    // =========================
    function onResults(results) {
        if (!necklace) return;

        if (!results.multiFaceLandmarks?.length) {
            necklace.visible = false;
            neckAnchorRef.current = null; // 🔴 reset anchor
            if (Date.now() - lastFaceTimeRef.current > 300) {
                setShowMessage(true);
            }
            return;
        }

        lastFaceTimeRef.current = Date.now();
        setShowMessage(false);

        const lm = results.multiFaceLandmarks[0];
        const chin = lm[152];
        const leftJaw = lm[234];
        const rightJaw = lm[454];

        const faceWidth = Math.abs(leftJaw.x - rightJaw.x);
        const neckX = (leftJaw.x + rightJaw.x) / 2;
        const neckY = chin.y + faceWidth * 0.9;

        const worldNeck = landmarkToWorld(neckX, neckY);

        // 🔴 CREATE ANCHOR ON FIRST DETECTION
        if (!neckAnchorRef.current) {
            neckAnchorRef.current = worldNeck.clone();
            smoothPosRef.current.copy(worldNeck);
        }

        // 🔴 SMALL MOVEMENT ONLY (CLAMP)
        const delta = worldNeck.clone().sub(neckAnchorRef.current);
        delta.clampLength(0, 0.08); // prevents jumping

        const targetPos = neckAnchorRef.current.clone().add(delta);

        // 🔴 STRONG SMOOTHING
        smoothPosRef.current.lerp(targetPos, 0.15);
        necklace.position.copy(smoothPosRef.current);

        // 🔴 SCALE (STABLE)
        const jawL = landmarkToWorld(leftJaw.x, leftJaw.y);
        const jawR = landmarkToWorld(rightJaw.x, rightJaw.y);
        const worldWidth = jawL.distanceTo(jawR);
        const scale = worldWidth * 0.9;

        necklace.scale.lerp(
            new THREE.Vector3(scale, scale, scale),
            0.2
        );

        // rotation
        const rotY = Math.atan2(
            rightJaw.z - leftJaw.z,
            rightJaw.x - leftJaw.x
        );
        necklace.rotation.y += (rotY - necklace.rotation.y) * 0.2;

        necklace.visible = true;
    }

    // =========================
    // RENDER LOOP
    // =========================
    function animate() {
        animationId = requestAnimationFrame(animate);
        renderer?.render(scene, camera);
    }

    // =========================
    // RESIZE
    // =========================
    function resizeRenderer() {
        if (!renderer || !canvasRef.current) return;

        const parent = canvasRef.current.parentElement;
        renderer.setSize(parent.clientWidth, parent.clientHeight);
        camera.aspect = parent.clientWidth / parent.clientHeight;
        camera.updateProjectionMatrix();
    }

    return (
        <>
            <video ref={videoRef} className="ar-video" autoPlay muted playsInline />
            <canvas ref={canvasRef} className="ar-canvas" />
            {showMessage && <div className="face-message">Please face the camera 👤</div>}
        </>
    );
}
